<template>
  <div>
    <img src="@/assets/heyitsme-icon.svg" class="back-btn-absolute" @click="$router.push('/')" alt="Home" role="button" />
    <h1 class="contact-title">Contact Me</h1>
    <div class="contact-page">
      <form class="contact-form" @submit.prevent="submitForm">
        <div class="form-group">
          <label for="email">Your Email Address:</label>
          <input type="email" id="email" v-model="form.email" required 
                 maxlength="254" />
        </div>
        <div class="form-group">
          <label for="type">Type of Message:</label>
          <select id="type" v-model="form.type" required>
            <option value="">Select...</option>
            <option value="issue">Issue Report</option>
            <option value="question">Question or Request</option>
          </select>
        </div>
        <div class="form-group">
          <label for="message">Message:</label>
          <textarea id="message" v-model="form.message" rows="5" required 
                    maxlength="2000"
                    placeholder="Please describe your issue or question in detail..."></textarea>
          <div class="char-count" :class="{ 'limit-reached': form.message.length >= 2000 }">
            {{ form.message.length }} / 2000
          </div>
        </div>
        <button type="submit" :disabled="isSubmitting">
          <span v-if="!isSubmitting">Send</span>
          <span v-else class="spinner"></span>
        </button>
      </form>
      <div v-if="submitted && !errorMessage" class="contact-success">
        Thank you for reaching out! I'll get back to you soon.
      </div>
      <div v-if="errorMessage" class="contact-error">
        {{ errorMessage }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref } from 'vue';
import { useApi } from '@/composables/useApi';

// Form state
const form = reactive({
  email: '',
  type: '',
  message: ''
});

// Component state
const submitted = ref(false);
const isSubmitting = ref(false);
const errorMessage = ref('');

// API composable
const api = useApi();

// Client-side input sanitization
const sanitizeEmail = (email) => {
  if (!email) return '';
  return email.trim().toLowerCase().replace(/[<>]/g, '');
};

const sanitizeText = (text) => {
  if (!text) return '';
  // Only remove the most dangerous characters, allow spaces and common punctuation
  return text.trim().replace(/[<>]/g, '').substring(0, 1000);
};

const sanitizeMessage = (message) => {
  if (!message) return '';
  // Allow spaces and common punctuation for messages
  // Only remove the most dangerous HTML/script characters
  let sanitized = message.replace(/[<>]/g, '');
  if (sanitized.length > 2000) {
    sanitized = sanitized.substring(0, 2000);
  }
  return sanitized;
};

const validateEmail = (email) => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

const submitForm = async () => {
  if (isSubmitting.value) return; // Prevent double-clicks

  // Clear previous states
  submitted.value = false;
  errorMessage.value = '';

  // Client-side sanitization and validation
  const sanitizedEmail = sanitizeEmail(form.email);
  const sanitizedType = sanitizeText(form.type);
  const sanitizedMessage = sanitizeMessage(form.message);

  // Validate inputs
  if (!validateEmail(sanitizedEmail)) {
    errorMessage.value = 'Please enter a valid email address.';
    return;
  }

  if (!sanitizedType || !['issue', 'question'].includes(sanitizedType)) {
    errorMessage.value = 'Please select a valid message type.';
    return;
  }

  if (!sanitizedMessage || sanitizedMessage.trim().length < 10) {
    errorMessage.value = 'Please enter a message with at least 10 characters.';
    return;
  }

  // Update form with sanitized values
  form.email = sanitizedEmail;
  form.type = sanitizedType;
  form.message = sanitizedMessage;

  isSubmitting.value = true;

  try {
    console.log('Submitting contact form...');

    await api.contact.submitForm(sanitizedEmail, sanitizedType, sanitizedMessage);
    
    console.log('Contact form submitted successfully');
    submitted.value = true;
    
    // Clear the form
    form.email = '';
    form.type = '';
    form.message = '';
  } catch (error) {
    console.error('Contact form submission failed:', error);
    errorMessage.value = 'Failed to send your message. Please try again or contact katy@gobeyonddev.com directly.';
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<style scoped>
.back-btn-absolute {
  position: absolute;
  top: 18px;
  left: 28px;
  z-index: 1000;
  width: 88px;
  height: 88px;
  object-fit: contain;
  cursor: pointer;
  transition: opacity 0.2s;
}
.back-btn-absolute:hover {
  opacity: 0.75;
}
@media (max-width: 700px) {
  .back-btn-absolute {
    top: 8px;
    left: 14px;
    width: 68px;
    height: 68px;
  }
}
.contact-page {
  max-width: 700px;
  margin: 8px auto 0 auto;
  padding: 24px 32px 24px 32px;
  background: #f5f5f5;
  border: 1px solid #e5e5e5; /* Match info boxes border */
  border-radius: 10px;        /* Match info boxes radius */
  box-shadow: 0 2px 12px rgba(0,0,0,0.04);
  text-align: left;
  box-sizing: border-box;
  flex: 1 0 auto;
}
@media (max-width: 700px) {
  .contact-page {
    max-width: 98vw;
    padding: 12px 6vw 12px 6vw;
  }
}
.contact-title {
  font-family: 'StayWonderful', 'Pacifico', 'Segoe Script', 'Brush Script MT', cursive;
  font-size: 2.5rem;
  color: #312d30;
  margin-bottom: 32px;
  text-align: center;
  font-weight: 300;
}
.contact-form {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
label {
  font-weight: bold;
  color: #312d30;
}
input, select, textarea {
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 16px; /* Prevent mobile zoom */
  font-family: 'Quicksand', 'Segoe UI', 'Georgia', serif;
}
.char-count {
  font-size: 0.9rem;
  color: #888;
  margin-top: 4px;
  text-align: right;
}
.char-count.limit-reached {
  color: #d97b63;
  font-weight: bold;
}
button[type="submit"] {
  background: #7da47e;
  color: #fff;
  border: none;
  padding: 10px 32px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1.13rem;
  font-weight: bold;
  align-self: center;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 120px;
}
button[type="submit"]:disabled {
  background: #ccc;
  cursor: not-allowed;
  opacity: 0.7;
}
button[type="submit"]:hover:not(:disabled) {
  background: #388E3C;
}
.contact-success {
  margin-top: 24px;
  color: #388E3C;
  font-weight: bold;
  font-size: 1.1rem;
  text-align: center;
}
.contact-error {
  margin-top: 24px;
  color: #d97b63;
  font-weight: bold;
  font-size: 1.1rem;
  text-align: center;
}
.contact-note {
  font-size: 1rem;
  font-family: 'Quicksand', 'Segoe UI', 'Georgia', serif;
  font-weight: 500;
  color: #312d30;
  letter-spacing: 0.01em;
  line-height: 1.7;
  margin-bottom: 0;
  text-align: center;
  max-width: 540px;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  word-break: break-word;
}
</style>
